import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Checkbox } from "../../../components/ui/checbox";
import { Spinner } from "../../../components/ui/spinner";
import { DEFAULT_USER_ROLE } from "../adapters";
import { USER_ROLE_OPTIONS } from "../constants";
import type { OrgUser } from "../hooks/useUsers";
import type {
  MCPSharingUserItem,
  MCPUserRole,
} from "../../../services/agent-tools-service";
import {
  enforceOrganizationFullAccess,
  requiresFullMcpAccess,
} from "../organizationAccess";
import { useTranslation } from "react-i18next";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  users: OrgUser[];
  isLoading: boolean;
  value: MCPSharingUserItem[];
  onConfirm: (users: MCPSharingUserItem[]) => void;
};

// Picking recipients happens in a modal rather than a dropdown: the list is
// tall enough to scroll for most orgs, and inside a popover that scroll was
// easy to miss. Each pick carries its own role, and the whole selection is
// drafted here so Cancel discards both the members and the roles.
const UserPickerDialog = ({
  open,
  onOpenChange,
  users,
  isLoading,
  value,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<MCPSharingUserItem[]>(value);
  const [search, setSearch] = useState("");

  // Seed on open only — making `value` a dependency would reset the draft
  // whenever the parent re-renders with a new array reference.
  const valueRef = useRef(value);
  valueRef.current = value;
  const usersRef = useRef(users);
  usersRef.current = users;
  useEffect(() => {
    if (open) {
      setDraft(
        enforceOrganizationFullAccess(valueRef.current, usersRef.current),
      );
      setSearch("");
    }
  }, [open]);

  const roleById = useMemo(() => {
    const map = new Map<string, MCPUserRole>();
    for (const user of draft) map.set(user.id, user.role);
    return map;
  }, [draft]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) => user.username.toLowerCase().includes(query));
  }, [search, users]);

  const toggle = (user: OrgUser) => {
    setDraft((prev) =>
      prev.some((selected) => selected.id === user.id)
        ? prev.filter((selected) => selected.id !== user.id)
        : [
            ...prev,
            {
              id: user.id,
              role: requiresFullMcpAccess(user)
                ? "owner"
                : DEFAULT_USER_ROLE,
            },
          ],
    );
  };

  const setRole = (id: string, role: MCPUserRole) => {
    if (requiresFullMcpAccess(users.find((user) => user.id === id))) return;
    setDraft((prev) =>
      prev.map((user) => (user.id === id ? { ...user, role } : user)),
    );
  };

  const selectAllFiltered = () => {
    setDraft((prev) => {
      const existing = new Set(prev.map((user) => user.id));
      const added = filtered
        .filter((user) => !existing.has(user.id))
        .map((user) => ({
          id: user.id,
          role: requiresFullMcpAccess(user) ? "owner" : DEFAULT_USER_ROLE,
        }));
      return [...prev, ...added];
    });
  };

  const handleConfirm = () => {
    onConfirm(enforceOrganizationFullAccess(draft, users));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[1500] gap-4 border-border bg-card sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {t("mcpPage.usersDialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("mcpPage.usersDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="relative flex items-center">
          <Search className="absolute start-3 size-4 text-muted-foreground" />
          <input
            type="text"
            autoFocus
            placeholder={t("mcpPage.usersDialog.searchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-10 w-full rounded-[8px] border border-border bg-card ps-9 pe-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
          />
        </div>

        <div className="flex items-center justify-between gap-3 text-xs">
          <span className="text-muted-foreground">
            {t("mcpPage.usersDialog.showing", {
              count: filtered.length,
              total: users.length,
            })}
          </span>
          <span className="flex items-center gap-3">
            <button
              type="button"
              onClick={selectAllFiltered}
              disabled={filtered.length === 0}
              className="text-primary hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("mcpPage.usersDialog.selectAll")}
            </button>
            <button
              type="button"
              onClick={() => setDraft([])}
              disabled={draft.length === 0}
              className="text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("mcpPage.usersDialog.clearAll")}
            </button>
          </span>
        </div>

        <div className="max-h-[min(50vh,320px)] overflow-y-auto rounded-[8px] border border-border">
          {isLoading && users.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Spinner className="size-4" />
              <span>{t("mcpPage.loadingUsers")}</span>
            </div>
          ) : filtered.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">
              {users.length === 0
                ? t("mcpPage.noOptions")
                : t("mcpPage.noMatchingUsers")}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((user) => {
                const role = roleById.get(user.id);
                const checked = role !== undefined;
                const mustKeepFullAccess = requiresFullMcpAccess(user);
                return (
                  <li
                    key={user.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-muted"
                  >
                    <label
                      htmlFor={`user-${user.id}`}
                      className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 py-0.5 text-sm text-foreground"
                    >
                      <Checkbox
                        id={`user-${user.id}`}
                        checked={checked}
                        onCheckedChange={() => toggle(user)}
                      />
                      <span className="truncate">{user.username}</span>
                    </label>

                    {/* The role picker only appears once someone is selected —
                        an empty row has no access level to set. */}
                    {checked && (
                      <Select
                        value={mustKeepFullAccess ? "owner" : role}
                        onValueChange={(next) =>
                          setRole(user.id, next as MCPUserRole)
                        }
                        disabled={mustKeepFullAccess}
                      >
                        <SelectTrigger className="h-8 w-[140px] shrink-0 rounded-[8px] border-border bg-card text-xs text-foreground">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[1600]">
                          {USER_ROLE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {t(`mcpPage.userRoles.${option.value}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {t("mcpPage.usersDialog.selectedCount", { count: draft.length })}
          </span>
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-9 rounded-[8px] border border-border bg-card px-4 text-sm text-foreground hover:bg-accent"
            >
              {t("mcpPage.actions.cancel")}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="h-9 rounded-[8px] bg-primary px-4 text-sm text-primary-foreground hover:brightness-110"
            >
              {t("mcpPage.usersDialog.done")}
            </button>
          </span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserPickerDialog;
